// Mantine theme: one static theme that adapts per color scheme.
//
// Mantine's `primaryShade` accepts per-scheme values, so a single theme serves
// both the light and black appearances. Semantic tokens (surfaces, borders,
// ink) are applied separately as CSS custom properties by `AppearanceProvider`,
// because they vary with the scheme in ways Mantine's palette does not model.

import {
  Combobox,
  createTheme,
  Popover,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type MantineThemeOverride,
} from '@mantine/core';
import { FONTS, RAMPS, blackTheme, lightTheme } from './tokens';

/** Pad a compact palette to the ten shades Mantine requires. */
function tuple(values: readonly string[]): MantineColorsTuple {
  const padded = [...values];
  const last = values[values.length - 1] ?? '#000000';
  while (padded.length < 10) padded.push(last);
  return padded.slice(0, 10) as unknown as MantineColorsTuple;
}

/** The single application theme. */
export const sensingTheme: MantineThemeOverride = createTheme({
  // The primary ramp differs per scheme: dark foliage on parchment, pale sage on
  // loam. `primaryShade` keeps both at the shade that is legible on its own
  // surface rather than reusing one index for two very different backgrounds.
  primaryColor: 'foliage',
  primaryShade: { light: 3, dark: 3 },
  fontFamily: FONTS.body,
  fontFamilyMonospace: FONTS.numeric,
  headings: {
    fontFamily: FONTS.hand,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '2.4rem', lineHeight: '1.15' },
      h2: { fontSize: '1.9rem', lineHeight: '1.2' },
      h3: { fontSize: '1.5rem', lineHeight: '1.25' },
      h4: { fontSize: '1.3rem', lineHeight: '1.3' },
      h5: { fontSize: '1.15rem', lineHeight: '1.35' },
      h6: { fontSize: '1.05rem', lineHeight: '1.4' },
    },
  },
  colors: {
    // Earthy names for the semantic accents, so a reader can tell what a colour
    // is for without decoding a conventional palette name.
    foliage: tuple(RAMPS.foliage),
    sage: tuple(RAMPS.sage),
    moss: tuple(RAMPS.moss),
    amber: tuple(RAMPS.amber),
    clay: tuple(RAMPS.clay),
    rust: tuple(RAMPS.rust),
    stone: tuple(RAMPS.stone),
    // Mantine resolves several internal colours from `gray`, including surfaces
    // behind disabled controls, so it has to be earthy too rather than its
    // default cool ramp.
    gray: tuple(RAMPS.stone),
    // Mantine draws dark-scheme surfaces, inputs and borders from `dark`
    // (body is shade 7, inputs 6, hover 5, borders 4, dimmed text 2, text 0).
    // Its default is a cool grey, which left the header, navigation and inputs
    // out of the loam palette.
    dark: tuple(RAMPS.loam),
    ink: tuple(lightTheme.colors.ink ?? []),
    paper: tuple(lightTheme.colors.paper ?? []),
    charcoal: tuple(blackTheme.colors.charcoal ?? []),
  },
  defaultRadius: 'md',
  cursorType: 'pointer',
  focusRing: 'auto',
  // On phones the page scrolls inside a fixed frame, not the document, so a
  // dropdown positioned in document coordinates lands below its input and is
  // hidden as detached. Positioning against the viewport is right for both
  // the fixed frame and the scrolling desktop document.
  components: {
    Popover: Popover.extend({ defaultProps: { floatingStrategy: 'fixed' } }),
    Combobox: Combobox.extend({ defaultProps: { floatingStrategy: 'fixed' } }),
  },
});

/**
 * Mantine's scheme-dependent surfaces, pointed at the palette. The light body
 * is parchment rather than Mantine's white, so the header and navigation match
 * the page; the dark values come from the `dark` ramp above.
 */
export const sensingCssVariables: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-anchor': lightTheme.cssVariables['--sensing-accent'] ?? '#556b2f',
    '--mantine-color-body': lightTheme.cssVariables['--sensing-surface'] ?? '#faf6ee',
    '--mantine-color-text': lightTheme.cssVariables['--sensing-ink'] ?? '#2a2419',
    '--mantine-color-default': lightTheme.cssVariables['--sensing-surface-raised'] ?? '#fffdf7',
    '--mantine-color-default-border':
      lightTheme.cssVariables['--sensing-border-strong'] ?? '#96896e',
  },
  dark: {
    '--mantine-color-anchor': blackTheme.cssVariables['--sensing-accent'] ?? '#b3c77e',
  },
});

export { lightTheme, blackTheme };
